'use client'

import { createContext, useContext } from 'react'

import type { FeatureFlag } from '@/server/domain/feature-flags'

interface FeatureFlagContextType {
  isEnabled: (flag: FeatureFlag) => boolean
}

const FeatureFlagContext = createContext<FeatureFlagContextType | undefined>(
  undefined,
)

/**
 * Provider that reads NEXT_PUBLIC_FEATURE_* env vars at build time
 * and exposes them to client components.
 */
export function FeatureFlagProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const flags: Record<string, boolean> = {
    timeline: process.env.NEXT_PUBLIC_FEATURE_TIMELINE === 'true',
  }

  const isEnabled = (flag: FeatureFlag): boolean => {
    return flags[flag] ?? false
  }

  return (
    <FeatureFlagContext.Provider value={{ isEnabled }}>
      {children}
    </FeatureFlagContext.Provider>
  )
}

/**
 * Hook to check if a feature flag is enabled in client components.
 *
 * @example
 * const { isEnabled } = useFeatureFlag()
 * if (!isEnabled('timeline')) return null
 */
export function useFeatureFlag() {
  const context = useContext(FeatureFlagContext)
  if (context === undefined) {
    throw new Error('useFeatureFlag must be used within a FeatureFlagProvider')
  }
  return context
}
